/**
 * One question being written: what its fields hold, what the wire will actually take, and whether it
 * may be added at all.
 *
 * The editor's „Nowe pytanie" card and the shelf's own dialog collect the SAME thing, and
 * `CustomQuestionRules.Validate` refuses the same things for both. The FIELDS are one component
 * (`ui/QuestionFields`); the three DECISIONS about them are here, because they are decisions and
 * because de-duplication is the only thing standing between „Tak, Tak" and a 400 — a rule nobody can
 * test while it lives in a `.tsx` beside a 4 × 2 grid of buttons.
 *
 * No React, no DOM, no query hook, no i18next.
 */

import {
  CHOICE_ANSWER_TYPES,
  MIN_CHOICE_OPTIONS,
  type AuthorableAnswerType,
} from './editorDraft';

/** What a question being written currently says. */
export interface QuestionFieldsValue {
  readonly label: string;
  readonly answerType: AuthorableAnswerType;
  readonly options: readonly string[];
  readonly required: boolean;
}

/** A blank card: a short text question with the two option rows a choice question would need. */
export const EMPTY_QUESTION_FIELDS: QuestionFieldsValue = {
  label: '',
  answerType: 'ShortText',
  options: ['', ''],
  required: false,
};

/**
 * The options as they will be SENT: trimmed, blanks dropped, de-duplicated.
 *
 * The web's copy of `CustomQuestionRules.NormalizeOptions`, asked before the round trip rather than
 * after it. The de-duplication is the load-bearing step: two rows both reading „Tak" are a question
 * whose answers cannot be told apart, and the server refuses it with a 400 that costs the trainer
 * everything else they had typed.
 *
 * A question of any other type carries no options at all, whatever is left in the rows.
 */
export function normalizedOptions(value: QuestionFieldsValue): readonly string[] {
  if (!CHOICE_ANSWER_TYPES.includes(value.answerType)) return [];
  return [...new Set(value.options.map((option) => option.trim()).filter((option) => option !== ''))];
}

/** Whether the question may be added at all. The same three refusals the backend makes. */
export function questionFieldsComplete(
  value: QuestionFieldsValue,
  options: { readonly clipsLeft: number },
): boolean {
  if (value.label.trim() === '') return false;
  if (CHOICE_ANSWER_TYPES.includes(value.answerType)) {
    return normalizedOptions(value).length >= MIN_CHOICE_OPTIONS;
  }
  if (value.answerType === 'ExerciseClip') return options.clipsLeft > 0;
  return true;
}
